<template>
  <div class="questionnaire-list">
    <v-dialog
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="questionnaireDialogOpened"
      persistent
      max-width="1000px"
      @keydown.esc="questionnaireDialogOpened = false"
    >
      <v-card v-if="selectedQuestionnaire">
        <v-card-title class="pb-0">
          <v-layout align-center>
            <div class="title mr-2 text-xs-left">
              {{ selectedQuestionnaire.formatted_name }}
            </div>
            <v-spacer />
            <v-btn
              :fab="$vuetify.breakpoint.xsOnly"
              small
              flat
              class="white error--text text-capitalize ma-0"
              @click="questionnaireDialogOpened = false"
            >
              <v-icon class="mr-1" size="18"> clear </v-icon>
              {{ $vuetify.breakpoint.xsOnly ? '' : $t('close') }}
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text
          v-if="
            !questionnaireDialogLoading &&
              selectedQuestionnaire.questionnaire_questions
          "
        >
          <smart-questionnaire-view
            :current-questionnaire="selectedQuestionnaire"
            :current-company="currentCompany"
            @openSpecificConversation="openRelevantConversation"
          />
        </v-card-text>
        <v-card-text v-else class="py-5">
          <v-progress-circular
            indeterminate
            size="25"
            color="primary"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- State: Loading -->
    <v-layout v-if="questionnairesLoading">
      <v-flex xs12>
        <v-progress-circular
          indeterminate
          size="40"
          color="primary"
        ></v-progress-circular>
      </v-flex>
    </v-layout>

    <!-- State: Alert -->
    <v-alert
      v-else-if="!questionnairesLoading && isEmpty(currentQuestionnairesReady)"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left ma-0"
    >
      {{ $t('dashboard.noCurrentQuestionnaires') }}
    </v-alert>

    <!-- State: View -->
    <v-card
      :raised="false"
      :key="questionnaire.id"
      class="card"
      v-for="questionnaire in currentQuestionnairesReady"
      v-else
    >
      <v-card-title class="item-title text-xs-left" primary-title pt-0>
        <h2>{{ questionnaire.tax_year }} {{ questionnaire.formatted_name }}</h2>
        <span v-if="questionnaire.locked" class="locked-span"
          ><img src="@/assets/svg/LockSimple.svg" height="14" width="14" />
          Locked
        </span>
        <v-spacer />
        <span
          v-if="
            currentFirm &&
              currentFirm.preferences &&
              currentFirm.preferences.custom_statuses_enabled &&
              findCustomStatus(questionnaire)
          "
          class="status-label title"
          >{{ findCustomStatus(questionnaire).client_display_status }}</span
        >
      </v-card-title>

      <v-card-text
        class="item-text text-xs-left"
        v-if="
          currentFirm &&
            currentFirm.preferences &&
            currentFirm.preferences.custom_statuses_enabled &&
            findCustomStatus(questionnaire) &&
            !isEmpty(findCustomStatus(questionnaire).client_instruction)
        "
        :value="true"
        type="info"
        outline
      >
        <span
          class="instruction"
          v-html="findCustomStatus(questionnaire).client_instruction"
        />
      </v-card-text>

      <v-card-text
        class="item-text text-xs-left"
        v-else-if="questionnaire.locked"
      >
        {{
          $t('dashboard.lockedQuestionnaireMessage', {
            questionnaireName: questionnaire.formatted_name,
          })
        }}
        {{ answerChangeCommunication }}
      </v-card-text>

      <v-card-text
        class="item-text text-xs-left"
        v-else-if="questionnaire.completed"
      >
        <span
          >{{
            $t('dashboard.completedQuestionnaireMessage', {
              questionnaireName: questionnaire.formatted_name,
            })
          }}
          {{ answerChangeCommunication }}</span
        >
      </v-card-text>
      <!--
      <v-card-text class="item-text text-xs-left" v-else-if="!questionnaire.completed">
        You've provided all information needed for your {{questionnaire.formatted_name}} but you haven't submitted it for review.<br/><br/>Click to open below.
      </v-card-text> -->

      <v-card-text
        class="item-text text-xs-left"
        v-else-if="
          questionnaire.opened_timestamp &&
            firstIncompleteSection(questionnaire)
        "
      >
        {{ firstIncompleteSection(questionnaire).instruction }}
      </v-card-text>
      <v-card-text class="item-text text-xs-left" v-else>
        {{
          $t('dashboard.questionnaireIntroductionMessage', {
            firmName: currentFirm.name,
            questionnaireName: questionnaire.formatted_name,
            dueDateWording: dueDateWording(questionnaire),
          })
        }}
        {{
          !isFirmCommunicationDisabled
            ? $t('dashboard.ifYouHaveAnyQuestionsPleaseSendUsAMessage')
            : ''
        }}
      </v-card-text>

      <v-card-text
        class="item-text text-xs-left"
        v-if="
          !questionnaire.locked &&
            !questionnaire.completed &&
            !questionnaire.is_open_item
        "
      >
        <v-layout>
          <v-flex>
            <v-stepper
              class="ma-0 pa-0 elevation-0 outlined"
              vertical
              :value="stepperIndex(questionnaire)"
            >
              <v-layout
                v-for="(section, i) in filteredQuestionnaireSections(
                  questionnaire,
                )"
                :key="section.id"
              >
                <v-stepper-step
                  :complete="sectionCompleted(questionnaire, section)"
                  :step="
                    questionnaire.sections.findIndex(s => s.id === section.id) +
                      1
                  "
                  class="ma-0 pa-2"
                >
                  {{ section.name }}
                </v-stepper-step>
              </v-layout>
            </v-stepper>
          </v-flex>
        </v-layout>
        <v-btn
          slot="activator"
          :block="$vuetify.breakpoint.xsOnly"
          class="text-none primary--text"
          @click="questionnaire.expandSteps = !questionnaire.expandSteps"
          flat
        >
          {{
            questionnaire.expandSteps
              ? $t('dashboard.minimize')
              : $t('dashboard.showAll')
          }}
        </v-btn>
      </v-card-text>

      <v-card-actions class="pa-3">
        <v-layout justify-between align-center wrap>
          <v-layout align-center wrap>
            <v-layout align-center v-if="questionnaireLoading(questionnaire)">
              <v-progress-circular
                indeterminate
                size="25"
                class="primary--text mr-1"
              />
              <span>{{ $t('dashboard.loading') }}..</span>
            </v-layout>

            <v-layout align-center wrap v-else>
              <v-btn
                slot="activator"
                :block="$vuetify.breakpoint.xsOnly"
                :color="
                  questionnaire.completed || questionnaire.locked
                    ? 'success'
                    : 'primary'
                "
                class="text-none action-button mr-4"
                @click="goToQuestionnaire(questionnaire)"
                data-test-id="questionnaire-action-button"
              >
                {{
                  questionnaire.completed || questionnaire.locked
                    ? $t('dashboard.view')
                    : questionnaire.opened_timestamp
                    ? $t('dashboard.continue')
                    : $t('dashboard.getStarted')
                }}
              </v-btn>

              <v-layout class="ma-2" v-if="questionnaire.locked">
                <v-icon class="success--text mr-1"> check_circle </v-icon>
                <span>
                  {{ $t('dashboard.reviewed') }}
                </span>
              </v-layout>
              <v-layout
                align-center
                class="ma-2"
                v-else-if="questionnaire.completed"
              >
                <v-icon class="success--text mr-1"> check_circle </v-icon>

                <span v-if="deliverySection(questionnaire)">
                  {{ $t('dashboard.completed') }}
                </span>
                <span v-else>
                  {{ $t('dashboard.inReview') }}
                </span>
              </v-layout>
            </v-layout>
          </v-layout>

          <div class="text-xs-left my-2">
            <span v-if="hideDueDate(questionnaire)">-</span>
            <v-layout
              class="error--text text--darken-2"
              v-else-if="
                questionnaire.due_date && daysUntilDue(questionnaire) === 0
              "
            >
              {{ $t('dashboard.dueToday') }}
            </v-layout>
            <v-layout
              class="warning--text text--darken-2"
              v-else-if="
                questionnaire.due_date && daysUntilDue(questionnaire) === 1
              "
            >
              {{ $t('dashboard.dueTomorrow') }}
            </v-layout>
            <v-layout
              class="error--text text--darken-2"
              v-else-if="
                questionnaire.due_date &&
                  moment(questionnaire.due_date) < moment()
              "
            >
              {{ $t('dashboard.overDue') }}
            </v-layout>
            <v-layout
              :class="
                daysUntilDue(questionnaire) > 2
                  ? 'grey--text text--darken-2'
                  : 'warning--text text--darken-2'
              "
              v-else
            >
              {{
                $t('dashboard.dueInDays', { days: daysUntilDue(questionnaire) })
              }}
            </v-layout>
          </div>
        </v-layout>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { mapState, mapGetters, mapActions } from 'vuex';
import SmartQuestionnaireView from '@/components/SmartQuestionnaireView';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';

export default {
  components: {
    SmartQuestionnaireView,
  },

  mixins: [QuestionnaireMixin],

  props: {
    currentCompanyId: {
      type: Number,
    },

    currentCompany: {
      type: Object,
    },

    shouldFocusEditor: {
      type: Boolean,
    },

    questionnaireIdToOpen: {
      type: Number,
    },

    yearFilter: {
      type: Number,
    },
  },

  data() {
    return {
      currentQuestionnaires: [],
      selectedQuestionnaire: null,
      questionnairesLoading: true,
      questionnaireDialogLoading: true,
      questionnaireDialogOpened: false,
      refreshTries: 0,
    };
  },

  methods: {
    ...mapActions('questionnaires', ['setTaxYearsOptions']),
    moment,
    isEmpty,
    findCustomStatus(questionnaire) {
      return this.currentFirm.firm_custom_statuses.find(
        firm_custom_status =>
          firm_custom_status.id === questionnaire.firm_custom_status_id,
      );
    },
    stepperIndex(questionnaire) {
      return questionnaire.sections.indexOf(
        Math.max(...this.completedSections(questionnaire).map(o => o.order)),
      );
    },
    completedSections(questionnaire) {
      return questionnaire.sections.filter(a =>
        this.sectionCompleted(questionnaire, a),
      );
    },
    firstIncompleteSection(questionnaire) {
      return questionnaire.sections
        .filter(s => !this.sectionCompleted(questionnaire, s))
        .sort((a, b) => a.order - b.order)[0];
    },
    filteredQuestionnaireSections(questionnaire) {
      if (questionnaire.expandSteps) {
        return questionnaire.sections;
      } else {
        const lastSection = questionnaire.sections
          .filter(s => !this.sectionCompleted(questionnaire, s))
          .sort((a, b) => a.order - b.order)[0];
        if (lastSection) {
          return questionnaire.sections
            .filter(
              s =>
                s.order - 1 == lastSection.order ||
                s.order == lastSection.order ||
                s.order - 2 == lastSection.order,
            )
            .sort((a, b) => a.order - b.order);
        } else {
          [];
        }
      }
    },

    dueDateWording(questionnaire) {
      return questionnaire && questionnaire.due_date
        ? ` by ${moment(questionnaire.due_date).format('LL')}`
        : '';
    },

    questionnaireLoading(questionnaire) {
      var createdAt = moment(questionnaire.created_at);
      return (
        (!questionnaire.locked && questionnaire.sections.length === 0) ||
        createdAt.add(5, 'seconds') > moment() ||
        questionnaire.processing
      );
    },

    hideDueDate(questionnaire) {
      return (
        !questionnaire.due_date ||
        questionnaire.completed ||
        questionnaire.locked
      );
    },

    daysUntilDue(questionnaire) {
      return moment(questionnaire.due_date)
        .startOf('day')
        .diff(moment().startOf('day'), 'days');
    },

    async openQuestionnaireDialog(questionnaire) {
      this.selectedQuestionnaire = questionnaire;
      this.questionnaireDialogLoading = true;
      this.questionnaireDialogOpened = true;
      try {
        const response = await API.questionnaire.getQuestionnaire({
          companyId: questionnaire.company_id,
          id: questionnaire.id,
        });
        this.selectedQuestionnaire = response.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.questionnaireDialogLoading = false;
      }
    },

    openRelevantConversation() {
      this.questionnaireDialogOpened = false;
      this.$emit('openRelevantConversation');
    },

    goToQuestionnaire(questionnaire) {
      if (questionnaire.processing) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Please wait up to a few minutes.',
        });
      } else {
        if (
          this.currentUser.readonly_access ||
          questionnaire.completed ||
          questionnaire.locked
        ) {
          this.openQuestionnaireDialog(questionnaire);
        } else {
          const company = this.companies.find(
            c => c.id === questionnaire.company_id,
          );

          if (company && company.readonly_access) {
            this.openQuestionnaireDialog(questionnaire);
          } else if (!company || this.currentUser.is_advisor) {
            this.$router.push(
              `/questionnaire?questionnaireId=${questionnaire.id}&companyId=${questionnaire.company_id}`,
            );
          } else {
            const hash =
              company.name
                .replaceAll(' ', '-')
                .replaceAll(',', '')
                .replaceAll('#', '') +
              '-' +
              company.hash_id;

            this.$router.push(
              `/questionnaire/${hash}/?questionnaireId=${questionnaire.id}`,
            );
          }
        }
      }
    },

    async refreshQuestionnaire() {
      this.questionnairesLoading = true;

      try {
        const response = await API.questionnaire.getClientQuestionnaires({
          companyId: this.currentCompanyId,
        });
        this.currentQuestionnaires = response.data
          .sort(function(a, b) {
            if (!a.locked && !b.locked) {
              if (!a.completed && !b.completed) {
                return a.due_date < b.due_date ? -1 : 1;
              } else {
                return !a.completed && b.completed ? -1 : 1;
              }
            } else {
              return !a.locked && b.locked ? -1 : 1;
            }
          })
          .map(q => {
            return {
              expandSteps: false,
              ...q,
            };
          });

        if (this.isClientDashboardTaxYearFilterEnabled) {
          const taxYears = uniq(
            this.currentQuestionnaires.map(
              questionnaire => questionnaire.tax_year,
            ),
          ).sort();
          this.setTaxYearsOptions(taxYears.reverse());
        }

        if (
          this.currentQuestionnaires.some(d => this.questionnaireLoading(d)) &&
          this.refreshTries < 20
        ) {
          await this.sleep(5000);
          this.refreshQuestionnaire();
          this.refreshTries = this.refreshTries + 1;
        } else {
          this.refreshTries = 0;
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem refreshing the questionnaires.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.questionnairesLoading = false;
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
      defaultCompany: 'defaultCompany',
      isClientDashboardTaxYearFilterEnabled:
        'isClientDashboardTaxYearFilterEnabled',
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
    }),
    ...mapState('companies', ['companies']),
    ...mapState('questionnaires', ['taxYears']),

    answerChangeCommunication() {
      return !this.isFirmCommunicationDisabled
        ? this.$t('dashboard.ifYouWantToChangeAnswerPleaseSendUsAMessage')
        : '';
    },

    shouldUseTaxYearFilter() {
      return (
        this.isClientDashboardTaxYearFilterEnabled ||
        (this.currentFirm &&
          this.currentFirm.preferences &&
          this.currentFirm.preferences.custom_statuses_enabled)
      );
    },

    currentQuestionnairesReady() {
      let result = this.currentQuestionnaires.filter(
        q => moment(q.created_at).add(5, 'seconds') < moment(),
      );

      if (this.shouldUseTaxYearFilter && this.yearFilter) {
        result = result.filter(
          questionnaire => questionnaire.tax_year === this.yearFilter,
        );
      }
      return result;
    },
  },

  async created() {
    try {
      await this.refreshQuestionnaire();
      if (this.questionnaireIdToOpen) {
        let questionnaireToOpen = this.currentQuestionnaires.find(
          q => q.id === this.questionnaireIdToOpen,
        );
        if (questionnaireToOpen) {
          this.openQuestionnaireDialog(questionnaireToOpen);
        }
        this.$emit('update:questionnaireIdToOpen', null);
      }
    } catch (e) {
      console.log(e);
    }
  },

  watch: {},
};
</script>

<style scoped lang="postcss">
.questionnaire-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.locked-span {
  border: 1px solid;
  border-color: #f18c8c;
  border-radius: 20px;
  padding-left: 12px;
  padding-right: 12px;
  color: #f18c8c;
  margin-left: 5px;

  display: flex;
  align-items: center;

  font-size: 13px;
}
.locked-span img {
  margin-right: 5px;
}
.item-title {
  padding: 18px 24px 6px;
  color: #333;
}

.item-text {
  padding: 6px 24px;
  font-size: 16px;
}

.action-button {
  & >>> .v-btn__content {
    font-weight: bold;
  }
}

.card {
  box-shadow: 0 1px 4px 0px rgba(0, 0, 0, 0.2);
}
.outlined {
  border: solid 2p x rgba(9, 30, 66, 0.06) !important;
}

.status-container {
  padding-left: 16px;
  margin-left: 2px;
  border-left: 4px solid #2196f3;
}
.status-label {
  color: #2196f3;
}
.instruction {
  font-size: 16px;
}
</style>
